// src/pages/rapim/RapimKeputusan.tsx — MODERN REDESIGN (FIXED)
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Edit, Trash2, Download, Target, Users,
  AlertTriangle, Building2, CheckCircle, Clock, Search, ChevronDown, ExternalLink, Sparkles, BarChart3, ArrowUpRight, Play, Award, FileText,
  DollarSign, CalendarCheck, UserCheck, MoreVertical
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Loading, TipTapEditor, RichTextContent } from '../../components/ui';
import { TindakLanjutRekomendasi, Unit, User } from '../../types';
import toast from 'react-hot-toast';
import { rapimService } from '../../services/rapim';
import { adminService } from '../../services/admin';
import { getPlainTextFromHtml, isRichTextMeaningful } from '../../utils/richText';

// ========== TYPES ==========
interface KeputusanForm {
  laporan_masalah_id: number | null;
  rekomendasi_tindak_lanjut: string;
  pic_unit_id: string;
  pic_user_id: string;
  target_date: string;
  budget: string;
  priority: 'rendah' | 'sedang' | 'tinggi' | 'kritis';
}

const emptyForm: KeputusanForm = {
  laporan_masalah_id: null,
  rekomendasi_tindak_lanjut: '',
  pic_unit_id: '',
  pic_user_id: '',
  target_date: '',
  budget: '',
  priority: 'sedang'
};

// ========== CONSTANTS ==========
const STATUS_CONFIG = {
  menunggu: {
    label: 'Menunggu',
    icon: Clock,
    gradient: 'from-gray-400 to-gray-500',
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
    dot: 'bg-gray-400'
  },
  berlangsung: {
    label: 'Berlangsung',
    icon: Play,
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-500 animate-pulse'
  },
  selesai: {
    label: 'Selesai',
    icon: CheckCircle,
    gradient: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  },
  terlambat: {
    label: 'Terlambat',
    icon: AlertTriangle,
    gradient: 'from-red-400 to-rose-500',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500'
  }
};

const PRIORITY_CONFIG = {
  rendah: { label: 'Rendah', emoji: '🟢', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  sedang: { label: 'Sedang', emoji: '🟡', color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  tinggi: { label: 'Tinggi', emoji: '🟠', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  kritis: { label: 'Kritis', emoji: '🔴', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' }
};

const TINDAK_LANJUT_FIELDS = [
  {
    key: 'program', label: 'Program', icon: '🎯', color: 'border-l-teal-500',
    desc: 'Program kerja yang direkomendasikan',
    tip: 'Jelaskan program/aksi konkret untuk menyelesaikan masalah. Contoh: "Pengadaan 20 unit komputer baru untuk lab Prodi TI."',
    bgTip: 'bg-teal-50 border-teal-200', textTip: 'text-teal-700'
  },
  {
    key: 'pengendalian', label: 'Pengendalian', icon: '🛡️', color: 'border-l-blue-500',
    desc: 'Mekanisme pengendalian pelaksanaan',
    tip: 'Jelaskan cara mengawal agar program berjalan sesuai rencana. Contoh: "Monitoring mingguan oleh Kaprodi, laporan progress tiap 2 minggu."',
    bgTip: 'bg-blue-50 border-blue-200', textTip: 'text-blue-700'
  },
  {
    key: 'evaluasi', label: 'Evaluasi', icon: '📊', color: 'border-l-amber-500',
    desc: 'Metode evaluasi keberhasilan',
    tip: 'Jelaskan bagaimana keberhasilan diukur. Contoh: "Survei kepuasan mahasiswa dan pengecekan fisik peralatan setelah instalasi."',
    bgTip: 'bg-amber-50 border-amber-200', textTip: 'text-amber-700'
  },
  {
    key: 'pengembangan', label: 'Pengembangan', icon: '🚀', color: 'border-l-emerald-500',
    desc: 'Rencana pengembangan lanjutan',
    tip: 'Jelaskan rencana peningkatan setelah program selesai. Contoh: "Pelatihan penggunaan peralatan baru dan penyusunan SOP pemeliharaan."',
    bgTip: 'bg-emerald-50 border-emerald-200', textTip: 'text-emerald-700'
  },
  {
    key: 'pelaporan', label: 'Pelaporan', icon: '📋', color: 'border-l-purple-500',
    desc: 'Mekanisme pelaporan progress',
    tip: 'Jelaskan bagaimana progress dilaporkan. Contoh: "Update progress via SIM-EVAL setiap minggu dengan bukti foto dan dokumen."',
    bgTip: 'bg-purple-50 border-purple-200', textTip: 'text-purple-700'
  },
];

void TINDAK_LANJUT_FIELDS;

const RECOMMENDATION_FIELD = {
  key: 'rekomendasi_tindak_lanjut',
  label: 'Rekomendasi Tindak Lanjut',
  icon: '📋',
  color: 'border-l-teal-500',
  desc: 'Arahan keputusan RAPIM yang akan dijalankan oleh PIC',
  tip: 'Tuliskan tindak lanjut secara lengkap, jelas, dan langsung bisa dikerjakan oleh PIC.',
  bgTip: 'bg-teal-50 border-teal-200',
  textTip: 'text-teal-700'
} as const;

const EXCLUDED_UNIT_TYPES = ['UNIVERSITAS', 'FAKULTAS', 'WAREK'];
const EXCLUDED_PIC_ROLES = ['rektor', 'dekan', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4', 'warek', 'lpm'];

// ========== HELPER FUNCTIONS ==========
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(value);
};

const formatCompactCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', {
    notation: 'compact',
    compactDisplay: 'short'
  }).format(value);
};

const formatRupiahInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// ========== SUB-COMPONENTS ==========

// Stat Card with modern gradient
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  gradient: string;
  trend?: { value: number; isUp: boolean };
}> = ({ icon, label, value, subValue, gradient, trend }) => (
  <div className="relative overflow-hidden rounded-2xl p-5 bg-white border border-neutral-border/50 hover:shadow-lg transition-all duration-300 group">
    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${gradient} opacity-10 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500`} />
    <div className="relative z-10">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-black text-neutral-text-primary tracking-tight">{value}</p>
      {subValue && <p className="text-xs text-neutral-text-secondary mt-0.5">{subValue}</p>}
      <p className="text-xs font-semibold text-neutral-text-secondary mt-1 uppercase tracking-wider">{label}</p>
      {trend && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-bold ${trend.isUp ? 'text-emerald-600' : 'text-red-500'}`}>
          <ArrowUpRight className={`w-3 h-3 ${!trend.isUp && 'rotate-90'}`} />
          {trend.value}% dari bulan lalu
        </div>
      )}
    </div>
  </div>
);

// Modern Keputusan Card
const KeputusanCard: React.FC<{
  keputusan: TindakLanjutRekomendasi;
  onEdit: () => void;
  onDelete: () => void;
  onViewDetail: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}> = ({ keputusan: k, onEdit, onDelete, onViewDetail, isExpanded, onToggleExpand }) => {
  const statusCfg = STATUS_CONFIG[k.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.menunggu;
  const priorityCfg = PRIORITY_CONFIG[k.priority as keyof typeof PRIORITY_CONFIG] || PRIORITY_CONFIG.sedang;
  const StatusIcon = statusCfg.icon;

  const budgetValue = k.budget ?? 0;
  const isOverdue = k.target_date && new Date(k.target_date) < new Date() && k.status !== 'selesai';
  const daysRemaining = k.target_date
    ? Math.ceil((new Date(k.target_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className={`
      group relative overflow-hidden rounded-2xl border-2 bg-white transition-all duration-300
      ${isExpanded ? 'shadow-xl border-teal-main/30' : 'border-neutral-border/50 hover:border-teal-main/20 hover:shadow-lg'}
    `}>
      {/* Gradient Accent */}
      <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${statusCfg.gradient}`} />

      {/* Main Content */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Progress Circle */}
          <div className="relative w-16 h-16 shrink-0">
            <svg className="w-full h-full -rotate-90">
              <circle
                cx="32" cy="32" r="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                className="text-neutral-bg"
              />
              <circle
                cx="32" cy="32" r="28"
                fill="none"
                stroke="url(#progressGradient)"
                strokeWidth="6"
                strokeDasharray={`${(k.progress_percentage / 100) * 176} 176`}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#14b8a6" />
                  <stop offset="100%" stopColor="#0d9488" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-black text-teal-main">{k.progress_percentage}%</span>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            {/* Tags Row */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2.5 py-1 rounded-lg font-bold">
                {k.laporan?.kode || '-'}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${statusCfg.bg} ${statusCfg.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                {statusCfg.label}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${priorityCfg.bg} ${priorityCfg.color} ${priorityCfg.border} border`}>
                {priorityCfg.emoji} {priorityCfg.label}
              </span>
              {isOverdue && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-lg animate-pulse">
                  ⚠️ Terlambat
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-base font-bold text-neutral-text-primary line-clamp-2 group-hover:text-teal-main transition-colors">
              {k.laporan?.judul || 'Keputusan RAPIM'}
            </h3>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-neutral-text-secondary">
              <span className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                {k.pic_unit?.name || '-'}
              </span>
              <span className="flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5" />
                {k.pic_user?.name || '-'}
              </span>
              <span className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
                <CalendarCheck className="w-3.5 h-3.5" />
                {k.target_date ? new Date(k.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                {daysRemaining !== null && daysRemaining > 0 && !isOverdue && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold ml-1">
                    {daysRemaining}h lagi
                  </span>
                )}
              </span>
              {budgetValue > 0 && (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5" />
                  {formatCurrency(budgetValue)}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onToggleExpand}
              className={`p-2 rounded-xl transition-all ${isExpanded ? 'bg-teal-main/10 text-teal-main' : 'hover:bg-neutral-bg text-neutral-text-secondary'}`}
              title={isExpanded ? 'Tutup detail' : 'Lihat detail tindak lanjut'}
            >
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            <div className="relative group/menu">
              <button className="p-2 rounded-xl hover:bg-neutral-bg text-neutral-text-secondary">
                <MoreVertical className="w-5 h-5" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-neutral-border/50 opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all z-20">
                <button onClick={onEdit} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-neutral-bg rounded-t-xl">
                  <Edit className="w-4 h-4" /> Edit Keputusan
                </button>
                <Link to={`/tindak-lanjut/${k.id}`} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-neutral-bg">
                  <ExternalLink className="w-4 h-4" /> Lihat Tindak Lanjut
                </Link>
                <button onClick={onDelete} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left text-red-600 hover:bg-red-50 rounded-b-xl">
                  <Trash2 className="w-4 h-4" /> Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-neutral-bg rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${statusCfg.gradient} transition-all duration-500`}
              style={{ width: `${k.progress_percentage}%` }}
            />
          </div>
          <span className="text-xs font-bold text-neutral-text-secondary w-12 text-right">
            {k.progress_percentage}%
          </span>
        </div>
      </div>

      {/* Expandable recommendation detail */}
      <div className={`
        overflow-hidden transition-all duration-500 ease-in-out
        ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}
      `}>
        <div className="px-5 pb-5 border-t border-neutral-border/50">
          <div className="pt-4">
            <div className={`relative overflow-hidden rounded-xl border-l-4 ${RECOMMENDATION_FIELD.color} bg-gradient-to-r from-neutral-bg/80 to-white p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{RECOMMENDATION_FIELD.icon}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary">
                  {RECOMMENDATION_FIELD.label}
                </span>
              </div>
              <RichTextContent
                content={k.rekomendasi_tindak_lanjut}
                className="text-sm text-neutral-text-primary"
                fallback="-"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-neutral-border/30">
            <Link
              to={`/tindak-lanjut/${k.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-main to-teal-600 text-white text-sm font-bold rounded-xl hover:shadow-lg hover:shadow-teal-main/25 transition-all active:scale-95"
            >
              <ExternalLink className="w-4 h-4" />
              Lihat Tindak Lanjut
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Filter Pills Component
const FilterPills: React.FC<{
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  counts: Record<string, number>;
}> = ({ activeFilter, onFilterChange, counts }) => {
  const filters = [
    { key: 'semua', label: 'Semua', icon: FileText },
    { key: 'berlangsung', label: 'Berlangsung', icon: Play },
    { key: 'selesai', label: 'Selesai', icon: CheckCircle },
    { key: 'terlambat', label: 'Terlambat', icon: AlertTriangle },
  ];

  return (
    <div className="flex items-center gap-1 p-1.5 bg-neutral-bg rounded-2xl overflow-x-auto">
      {filters.map((filter) => {
        const Icon = filter.icon;
        const isActive = activeFilter === filter.key;
        return (
          <button
            key={filter.key}
            onClick={() => onFilterChange(filter.key)}
            className={`
              inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
              whitespace-nowrap transition-all duration-200
              ${isActive
                ? 'bg-white text-teal-main shadow-md'
                : 'text-neutral-text-secondary hover:text-neutral-text-primary hover:bg-white/50'
              }
            `}
          >
            <Icon className="w-4 h-4" />
            {filter.label}
            {counts[filter.key] !== undefined && (
              <span className={`
                min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold
                ${isActive ? 'bg-teal-main/10 text-teal-main' : 'bg-neutral-border/50'}
              `}>
                {counts[filter.key]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// Empty State
const EmptyState: React.FC<{ filter: string }> = ({ filter }) => (
  <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-neutral-bg to-white flex items-center justify-center mb-6 shadow-inner">
      {filter === 'semua' ? (
        <Award className="w-12 h-12 text-neutral-text-secondary opacity-40" />
      ) : filter === 'terlambat' ? (
        <AlertTriangle className="w-12 h-12 text-amber-300" />
      ) : (
        <CheckCircle className="w-12 h-12 text-emerald-300" />
      )}
    </div>
    <h3 className="text-xl font-black text-neutral-text-primary mb-2">
      {filter === 'semua' ? 'Belum Ada Keputusan' :
        filter === 'terlambat' ? 'Tidak Ada yang Terlambat 🎉' :
          'Tidak Ada Data'}
    </h3>
    <p className="text-sm text-neutral-text-secondary max-w-md">
      {filter === 'semua'
        ? 'Keputusan tindak lanjut akan muncul setelah dibuat dari halaman detail RAPIM.'
        : filter === 'terlambat'
          ? 'Semua keputusan berjalan sesuai jadwal. Pertahankan kinerja ini!'
          : 'Tidak ada keputusan dengan status ini.'}
    </p>
  </div>
);

// ========== MAIN COMPONENT ==========
const RapimKeputusan: React.FC = () => {
  const { id } = useParams();
  const [keputusan, setKeputusan] = useState<TindakLanjutRekomendasi[]>([]);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<Unit[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<KeputusanForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState('semua');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  // ========== FETCH DATA ==========
  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [kepRes, unitsRes, usersRes] = await Promise.all([
        rapimService.getKeputusanByRapat(Number(id)),
        adminService.units.getAll(),
        adminService.users.getAll(),
      ]);

      const kepData = (kepRes as any)?.data || kepRes;
      setKeputusan(Array.isArray(kepData) ? kepData : (kepData?.data || []));

      const unitsData = (unitsRes as any)?.data || unitsRes;
      const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);
      setUnits(allUnits.filter((u: any) => !EXCLUDED_UNIT_TYPES.includes((u.type || '').toUpperCase())));

      const usersData = (usersRes as any)?.data || usersRes;
      const allUsers = Array.isArray(usersData) ? usersData : (usersData?.data || []);
      setUsers(allUsers.filter((u: any) => !EXCLUDED_PIC_ROLES.includes((u.role?.name || '').toLowerCase())));
    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat data keputusan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  // ========== COMPUTED ==========
  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: keputusan.length,
      berlangsung: keputusan.filter(k => k.status === 'berlangsung').length,
      selesai: keputusan.filter(k => k.status === 'selesai').length,
      terlambat: keputusan.filter(k => k.target_date && new Date(k.target_date) < now && k.status !== 'selesai').length,
      avgProgress: keputusan.length > 0 ? Math.round(keputusan.reduce((s, k) => s + k.progress_percentage, 0) / keputusan.length) : 0,
      totalBudget: keputusan.reduce((s, k) => s + (k.budget ?? 0), 0)
    };
  }, [keputusan]);

  const filterCounts = useMemo(() => ({
    semua: stats.total,
    berlangsung: stats.berlangsung,
    selesai: stats.selesai,
    terlambat: stats.terlambat
  }), [stats]);

  const filteredKeputusan = useMemo(() => {
    let filtered = keputusan;

    // Status filter
    if (activeFilter === 'berlangsung') {
      filtered = filtered.filter(k => k.status === 'berlangsung');
    } else if (activeFilter === 'selesai') {
      filtered = filtered.filter(k => k.status === 'selesai');
    } else if (activeFilter === 'terlambat') {
      const now = new Date();
      filtered = filtered.filter(k => k.target_date && new Date(k.target_date) < now && k.status !== 'selesai');
    }

    // Search filter
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(k =>
        k.laporan?.judul?.toLowerCase().includes(s) ||
        k.laporan?.kode?.toLowerCase().includes(s) ||
        k.pic_unit?.name?.toLowerCase().includes(s) ||
        k.pic_user?.name?.toLowerCase().includes(s) ||
        getPlainTextFromHtml(k.rekomendasi_tindak_lanjut).toLowerCase().includes(s)
      );
    }

    return filtered;
  }, [keputusan, activeFilter, searchTerm]);

  // ========== HANDLERS ==========
  const openEdit = (k: TindakLanjutRekomendasi) => {
    setEditingId(k.id);
    setForm({
      laporan_masalah_id: k.laporan_masalah_id,
      rekomendasi_tindak_lanjut: k.rekomendasi_tindak_lanjut || '',
      pic_unit_id: k.pic_unit_id.toString(),
      pic_user_id: k.pic_user_id?.toString() || '',
      target_date: k.target_date?.split('T')[0] || '',
      budget: (k.budget ?? 0).toString(),
      priority: (k.priority as any) || 'sedang'
    });
    setCurrentStep(1);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!id) return;

    const errors: string[] = [];
    if (!isRichTextMeaningful(form.rekomendasi_tindak_lanjut, 10)) {
      errors.push('Rekomendasi tindak lanjut minimal 10 karakter');
    }
    if (!form.pic_unit_id) errors.push('Unit PIC wajib dipilih');
    if (!form.pic_user_id) errors.push('Penanggung jawab wajib dipilih');
    if (!form.target_date) errors.push('Target tanggal wajib diisi');

    if (errors.length > 0) {
      toast.error(errors.slice(0, 3).join('\n'), { duration: 4000 });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        rapat_id: Number(id),
        laporan_masalah_id: form.laporan_masalah_id!,
        rekomendasi_tindak_lanjut: form.rekomendasi_tindak_lanjut,
        pic_unit_id: Number(form.pic_unit_id),
        pic_user_id: Number(form.pic_user_id),
        target_date: form.target_date,
        budget: Number(form.budget.replace(/\D/g, '')) || 0,
        priority: form.priority
      };

      if (editingId) {
        await rapimService.updateKeputusan(editingId, payload as any);
        toast.success('Keputusan berhasil diperbarui! ✅');
      } else {
        await rapimService.createKeputusan(payload as any);
        toast.success('Keputusan tindak lanjut berhasil dibuat! 🎉');
      }
      setShowModal(false);
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal menyimpan keputusan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (keputusanId: number) => {
    if (window.confirm('Yakin ingin menghapus keputusan ini? Tindakan ini tidak dapat dibatalkan.')) {
      try {
        await rapimService.deleteKeputusan(keputusanId);
        toast.success('Keputusan dihapus');
        fetchData();
      } catch (e) {
        toast.error('Gagal menghapus keputusan');
      }
    }
  };

  const handleExportPDF = async () => {
    if (!id) return;
    try {
      const arrayBuffer = await rapimService.exportPDF(Number(id), 'hasil');
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `KEPUTUSAN-RAPIM-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF berhasil diunduh');
    } catch (e) {
      toast.error('Gagal export PDF');
    }
  };

  const filteredUsers = useMemo(() => {
    if (!form.pic_unit_id) return [];
    return users.filter(u => u.unit?.id === Number(form.pic_unit_id));
  }, [users, form.pic_unit_id]);

  const isRecommendationValid = isRichTextMeaningful(form.rekomendasi_tindak_lanjut, 10);

  // ========== LOADING ==========
  if (loading) {
    return <Loading variant="page" message="Memuat keputusan RAPIM..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Detail Rapat', href: `/rapim/${id}` },
        { label: 'Keputusan Tindak Lanjut' },
      ]} />

      {/* ===== HERO HEADER ===== */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-dark via-[#0a1628] to-teal-main p-6 md:p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-teal-light/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-6 h-6 text-teal-light" />
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-light/80">
                Keputusan Tindak Lanjut
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              Manajemen Keputusan
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Pantau progress dan kelola keputusan RAPIM
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              icon={<Download className="w-4 h-4" />}
              className="!border-white/20 !text-white hover:!bg-white/10"
            >
              Export PDF
            </Button>
            <Link to={`/rapim/${id}`}>
              <Button variant="primary" size="sm" className="!bg-white !text-teal-dark">
                Kembali ke RAPIM
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* ===== STAT CARDS ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<FileText className="w-5 h-5" />}
          label="Total Keputusan"
          value={stats.total}
          gradient="from-teal-500 to-teal-600"
        />
        <StatCard
          icon={<Play className="w-5 h-5" />}
          label="Berlangsung"
          value={stats.berlangsung}
          gradient="from-amber-500 to-orange-500"
        />
        <StatCard
          icon={<BarChart3 className="w-5 h-5" />}
          label="Rata-rata Progress"
          value={`${stats.avgProgress}%`}
          gradient="from-blue-500 to-indigo-600"
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5" />}
          label="Total Anggaran"
          value={formatCompactCurrency(stats.totalBudget)}
          subValue={formatCurrency(stats.totalBudget)}
          gradient="from-emerald-500 to-green-600"
        />
      </div>

      {/* ===== FILTER & SEARCH ===== */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
          <input
            type="text"
            placeholder="Cari keputusan berdasarkan judul, kode, unit, atau PIC..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-neutral-border bg-white text-sm
              focus:outline-none focus:border-teal-main transition-colors"
          />
        </div>
        <FilterPills
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={filterCounts}
        />
      </div>

      {/* ===== KEPUTUSAN LIST ===== */}
      <div className="space-y-4">
        {filteredKeputusan.length === 0 ? (
          <Card className="!p-0">
            <EmptyState filter={activeFilter} />
          </Card>
        ) : (
          filteredKeputusan.map((k) => (
            <KeputusanCard
              key={k.id}
              keputusan={k}
              onEdit={() => openEdit(k)}
              onDelete={() => handleDelete(k.id)}
              onViewDetail={() => { }}
              isExpanded={expandedId === k.id}
              onToggleExpand={() => setExpandedId(expandedId === k.id ? null : k.id)}
            />
          ))
        )}
      </div>

      {/* ===== EDIT MODAL ===== */}
      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editingId ? '✏️ Edit Keputusan Tindak Lanjut' : '📋 Buat Keputusan Tindak Lanjut'}
        size="xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((step) => (
                <button
                  key={step}
                  onClick={() => setCurrentStep(step)}
                  className={`
                    w-8 h-8 rounded-full text-xs font-bold transition-all
                    ${currentStep === step
                      ? 'bg-teal-main text-white'
                      : currentStep > step
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-neutral-bg text-neutral-text-secondary'
                    }
                  `}
                >
                  {currentStep > step ? '✓' : step}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {currentStep > 1 && (
                <Button variant="ghost" onClick={() => setCurrentStep(currentStep - 1)}>
                  Kembali
                </Button>
              )}
              {currentStep < 3 ? (
                <Button variant="primary" onClick={() => setCurrentStep(currentStep + 1)}>
                  Lanjut
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSave}
                  loading={saving}
                  icon={<CheckCircle className="w-4 h-4" />}
                  disabled={!form.pic_unit_id || !form.pic_user_id || !form.target_date || !isRecommendationValid}
                >
                  {editingId ? 'Perbarui' : 'Simpan'}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-8 py-4 border-b border-neutral-border/50">
            {[
              { step: 1, label: 'Penugasan', icon: Users },
              { step: 2, label: 'Detail Tindak Lanjut', icon: FileText },
              { step: 3, label: 'Target', icon: Target },
            ].map((item, index) => {
              const Icon = item.icon;
              const isActive = currentStep === item.step;
              const isComplete = currentStep > item.step;
              return (
                <React.Fragment key={item.step}>
                  <div className="flex flex-col items-center gap-2">
                    <div className={`
                      w-10 h-10 rounded-xl flex items-center justify-center transition-all
                      ${isActive ? 'bg-teal-main text-white shadow-lg shadow-teal-main/30' :
                        isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-neutral-bg text-neutral-text-secondary'}
                    `}>
                      {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs font-semibold ${isActive ? 'text-teal-main' : 'text-neutral-text-secondary'}`}>
                      {item.label}
                    </span>
                  </div>
                  {index < 2 && (
                    <div className={`w-16 h-0.5 rounded ${currentStep > item.step ? 'bg-emerald-500' : 'bg-neutral-border'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step 1: Penugasan */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl border border-blue-200/50">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tips:</strong> Pilih unit dan penanggung jawab yang akan melaksanakan keputusan ini.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                    Unit PIC <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`
                      w-full rounded-xl px-4 py-3 text-sm bg-white border-2 focus:outline-none focus:ring-2 transition-all
                      ${!form.pic_unit_id ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-border focus:border-teal-main'}
                    `}
                    value={form.pic_unit_id}
                    onChange={(e) => setForm({ ...form, pic_unit_id: e.target.value, pic_user_id: '' })}
                  >
                    <option value="">Pilih unit...</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id.toString()}>{u.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                    Penanggung Jawab <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`
                      w-full rounded-xl px-4 py-3 text-sm bg-white border-2 focus:outline-none focus:ring-2 transition-all
                      ${!form.pic_user_id ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-border focus:border-teal-main'}
                    `}
                    value={form.pic_user_id}
                    onChange={(e) => setForm({ ...form, pic_user_id: e.target.value })}
                    disabled={!form.pic_unit_id}
                  >
                    <option value="">{form.pic_unit_id ? 'Pilih PIC...' : 'Pilih unit dulu'}</option>
                    {filteredUsers.map((u) => (
                      <option key={u.id} value={u.id.toString()}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                  Prioritas
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm({ ...form, priority: key as any })}
                      className={`
                        p-3 rounded-xl border-2 text-center transition-all
                        ${form.priority === key
                          ? `${cfg.bg} ${cfg.border} shadow-md scale-[1.02]`
                          : 'border-neutral-border hover:border-neutral-text-secondary'
                        }
                      `}
                    >
                      <span className="text-xl">{cfg.emoji}</span>
                      <p className={`text-xs font-bold mt-1 ${form.priority === key ? cfg.color : ''}`}>
                        {cfg.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Detail tindak lanjut */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200/50">
                <p className="text-sm text-purple-800">
                  <strong>📝 Rekomendasi:</strong> Tulis satu rekomendasi tindak lanjut yang jelas dan dapat dijalankan.
                </p>
              </div>

              <div className={`border-l-4 ${RECOMMENDATION_FIELD.color} pl-4 py-2`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{RECOMMENDATION_FIELD.icon}</span>
                  <label className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                    {RECOMMENDATION_FIELD.label} <span className="text-danger">*</span>
                  </label>
                </div>
                <p className="text-[10px] text-neutral-text-secondary mb-2">{RECOMMENDATION_FIELD.desc}</p>
                  <TipTapEditor
                    content={form.rekomendasi_tindak_lanjut}
                    onChange={(content) => setForm({ ...form, rekomendasi_tindak_lanjut: content })}
                    placeholder="Tuliskan rekomendasi tindak lanjut yang jelas, runtut, dan langsung bisa dijalankan..."
                    minHeight="240px"
                    className="bg-white"
                  />
                  {/* TAMBAH: Tips panduan */}
                  <div className={`mt-1.5 p-2.5 rounded-lg border ${RECOMMENDATION_FIELD.bgTip}`}>
                    <p className={`text-[11px] leading-relaxed ${RECOMMENDATION_FIELD.textTip}`}>
                      <span className="font-bold">💡 Panduan:</span>{' '}
                      {RECOMMENDATION_FIELD.tip}
                    </p>
                  </div>
                </div>
            </div>
          )}

          {/* Step 3: Target */}
          {currentStep === 3 && (
            <div className="space-y-5">
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200/50">
                <p className="text-sm text-emerald-800">
                  <strong>🎯 Terakhir:</strong> Tentukan target waktu dan estimasi anggaran.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                    Target Tanggal <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`
                      w-full rounded-xl px-4 py-3 text-sm bg-white border-2 focus:outline-none focus:ring-2 transition-all
                      ${!form.target_date ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-border focus:border-teal-main'}
                    `}
                    value={form.target_date}
                    onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                    Estimasi Anggaran (Rp)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full rounded-xl px-4 py-3 text-sm bg-white border-2 border-neutral-border focus:border-teal-main focus:outline-none transition-all"
                    placeholder="0"
                    value={formatRupiahInput(form.budget)}
                    onChange={(e) => setForm({ ...form, budget: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
              </div>

              {/* Summary Preview */}
              <Card className="!bg-gradient-to-r from-neutral-bg to-white">
                <h4 className="text-sm font-bold text-neutral-text-primary mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-teal-main" />
                  Ringkasan Keputusan
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-white rounded-xl border border-neutral-border/50 text-center">
                    <Building2 className="w-5 h-5 mx-auto text-neutral-text-secondary mb-1" />
                    <p className="text-[10px] text-neutral-text-secondary">Unit</p>
                    <p className={`text-xs font-bold truncate ${form.pic_unit_id ? 'text-teal-main' : 'text-danger'}`}>
                      {form.pic_unit_id ? units.find(u => u.id.toString() === form.pic_unit_id)?.name : '—'}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-neutral-border/50 text-center">
                    <UserCheck className="w-5 h-5 mx-auto text-neutral-text-secondary mb-1" />
                    <p className="text-[10px] text-neutral-text-secondary">PIC</p>
                    <p className={`text-xs font-bold truncate ${form.pic_user_id ? 'text-teal-main' : 'text-danger'}`}>
                      {form.pic_user_id ? users.find(u => u.id.toString() === form.pic_user_id)?.name : '—'}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-neutral-border/50 text-center">
                    <CalendarCheck className="w-5 h-5 mx-auto text-neutral-text-secondary mb-1" />
                    <p className="text-[10px] text-neutral-text-secondary">Target</p>
                    <p className={`text-xs font-bold ${form.target_date ? 'text-teal-main' : 'text-danger'}`}>
                      {form.target_date ? new Date(form.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '—'}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-xl border border-neutral-border/50 text-center">
                    <DollarSign className="w-5 h-5 mx-auto text-neutral-text-secondary mb-1" />
                    <p className="text-[10px] text-neutral-text-secondary">Anggaran</p>
                    <p className="text-xs font-bold text-teal-main">
                      {form.budget ? `Rp ${formatRupiahInput(form.budget)}` : '—'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RapimKeputusan;
